import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  verifyOtp,
  resendOtp,
  clearError,
  selectAuthLoading,
  selectAuthError
} from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';
import {
  Phone,
  KeyRound,
  CheckCircle,
  XCircle,
  LoaderCircle,
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  Clock,
  Send
} from 'lucide-react';

const VerifyOtp = ({ showToast }) => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');

  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  // Auto‑fill phone from location state or query param
  useEffect(() => {
    const state = location.state;
    if (state?.phone) {
      setPhone(state.phone);
      if (state.autoSend !== false) {
        // Registration flow – auto‑send OTP
        handleSendOtp(state.phone);
      } else {
        // Login flow – OTP already sent by backend
        if (state.message) {
          setInfoMessage(state.message);
        } else {
          setInfoMessage('An OTP has been sent to your phone. Please check your messages.');
        }
        // ✅ Enable verification because OTP is already sent
        setOtpSent(true);
      }
    } else {
      const params = new URLSearchParams(location.search);
      const phoneParam = params.get('phone');
      if (phoneParam) {
        setPhone(phoneParam);
        if (state?.autoSend !== false) {
          handleSendOtp(phoneParam);
        }
      }
    }
  }, [location]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
  }, []);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(0, 1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    if (digit && index < 3) {
      inputRefs[index + 1].current.focus();
    }
  };

  // Handle backspace key
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otp[index] === '' && index > 0) {
        inputRefs[index - 1].current.focus();
      } else if (otp[index] !== '') {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
  };

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length && i < 4; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      const nextIndex = Math.min(pastedData.length, 3);
      if (inputRefs[nextIndex].current) {
        inputRefs[nextIndex].current.focus();
      }
    }
  };

  // Send OTP (initial or resend)
  const handleSendOtp = async (phoneNumber = phone) => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setVerificationError('Please enter a valid phone number.');
      return;
    }

    setIsResending(true);
    setResendError('');
    setResendSuccess(false);
    setVerificationError('');
    setInfoMessage('');

    try {
      const result = await dispatch(resendOtp({ phone: phoneNumber })).unwrap();
      if (result.success) {
        setOtpSent(true);
        setResendSuccess(true);
        setCountdown(60);
        setOtp(['', '', '', '']);
        if (inputRefs[0].current) {
          inputRefs[0].current.focus();
        }
        if (showToast) {
          showToast('✅ OTP sent to your phone!', 'success');
        }
      } else {
        setResendError(result.message || 'Failed to send OTP');
      }
    } catch (err) {
      setResendError(err.message || 'Failed to send OTP');
      if (showToast) {
        showToast(err.message || 'Failed to send OTP', 'error');
      }
    } finally {
      setIsResending(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (!phone || phone.length < 10) {
      setVerificationError('Please enter a valid phone number.');
      return;
    }
    if (otpString.length !== 4) {
      setVerificationError('Please enter the complete 4‑digit OTP.');
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      const result = await dispatch(verifyOtp({ phone, otp: otpString })).unwrap();
      if (result.success) {
        setIsVerified(true);
        if (showToast) {
          showToast('✅ Phone number verified successfully!', 'success');
        }
        setTimeout(() => {
          navigate('/login', {
            state: { message: 'Phone verified successfully. You can now login.' }
          });
        }, 3000);
      } else {
        setVerificationError(result.message || 'Verification failed');
      }
    } catch (err) {
      setIsVerified(false);
      const msg = err.message || 'Failed to verify OTP';
      setVerificationError(msg);
      if (showToast) {
        showToast(msg, 'error');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP handler
  const handleResendClick = () => {
    if (isResending || countdown > 0) return;
    handleSendOtp(phone);
  };

  // Phone change handler – reset OTP sent state
  const handlePhoneChange = (e) => {
    setPhone(e.target.value);
    setOtpSent(false);
    setOtp(['', '', '', '']);
    setVerificationError('');
    setResendError('');
    setResendSuccess(false);
    setInfoMessage('');
  };

  // Loading state
  if (isVerifying) {
    return (
      <AuthLayout title="Verifying OTP" subtitle="Please wait while we verify your code">
        <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center', padding: '40px 0' }}>
          <LoaderCircle size={48} style={{ animation: 'spin 1s linear infinite', color: '#d4af37' }} />
          <p style={{ marginTop: '20px', color: '#64748b', fontSize: '16px' }}>
            Verifying your OTP...
          </p>
        </div>
      </AuthLayout>
    );
  }

  // Verified state
  if (isVerified) {
    return (
      <AuthLayout title="Phone Verified!" subtitle="Your phone number has been successfully verified">
        <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center', padding: '20px 0' }}>
          <div style={{
            marginBottom: '24px',
            padding: '40px',
            backgroundColor: '#d1fae5',
            borderRadius: '50%',
            width: '100px',
            height: '100px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircle size={60} color="#065f46" />
          </div>
          <h2 style={{ color: '#1a1a2e', marginBottom: '12px' }}>
            Phone Verified Successfully!
          </h2>
          <p style={{ color: '#64748b', fontSize: '15px', marginBottom: '24px' }}>
            Your phone number has been verified. You can now log in to your account.
          </p>
          <Link
            to="/login"
            style={{
              display: 'block',
              width: '100%',
              padding: '18px',
              fontSize: '18px',
              fontWeight: '700',
              borderRadius: '10px',
              backgroundColor: '#d4af37',
              color: '#1a1a2e',
              border: 'none',
              textAlign: 'center',
              textDecoration: 'none',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#c5a032'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#d4af37'}
          >
            Proceed to Login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Main verification form
  return (
    <AuthLayout title="Verify Your Phone" subtitle="Enter the OTP sent to your phone">
      <form onSubmit={handleVerifyOtp} style={{ maxWidth: '520px', margin: '0 auto' }}>
        {/* Info Message */}
        {infoMessage && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#dbeafe',
            color: '#1e40af',
            border: '1px solid #bfdbfe',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <AlertTriangle size={20} />
            <span>{infoMessage}</span>
          </div>
        )}

        {/* Error / Success Messages */}
        {verificationError && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <XCircle size={20} />
            <span>{verificationError}</span>
          </div>
        )}

        {resendSuccess && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            border: '1px solid #a7f3d0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <CheckCircle size={20} />
            <span>OTP sent successfully! Please check your phone.</span>
          </div>
        )}

        {resendError && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <XCircle size={20} />
            <span>{resendError}</span>
          </div>
        )}

        {/* Phone Number Field */}
        <div style={{ marginBottom: '20px', position: 'relative' }}>
          <label style={{
            display: 'block',
            fontWeight: '600',
            marginBottom: '8px',
            color: '#1a1a2e',
            fontSize: '15px'
          }}>
            Phone Number
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '18px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#94a3b8',
              pointerEvents: 'none',
              zIndex: 1
            }}>
              <Phone size={20} />
            </span>
            <input
              type="tel"
              value={phone}
              onChange={handlePhoneChange}
              style={{
                width: '100%',
                padding: '16px 20px 16px 50px',
                fontSize: '17px',
                borderRadius: '10px',
                border: '2px solid #e2e8f0',
                background: '#ffffff',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                color: '#1a1a2e',
                outline: 'none',
                lineHeight: '1.5'
              }}
              placeholder="Enter your phone number"
              required
              disabled={loading || isVerifying}
              onFocus={(e) => e.target.style.borderColor = '#d4af37'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>
        </div>

        {/* OTP Code - Modern Separate Inputs */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#1a1a2e',
            fontSize: '15px'
          }}>
            OTP Code
          </label>
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center'
          }}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs[index]}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={loading || isVerifying}
                style={{
                  width: '60px',
                  height: '70px',
                  fontSize: '28px',
                  fontWeight: '600',
                  textAlign: 'center',
                  borderRadius: '12px',
                  border: `2px solid ${verificationError ? '#dc2626' : '#e2e8f0'}`,
                  background: '#ffffff',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  color: '#1a1a2e',
                  outline: 'none',
                  lineHeight: '1'
                }}
                onFocus={(e) => e.target.style.borderColor = '#d4af37'}
                onBlur={(e) => e.target.style.borderColor = verificationError ? '#dc2626' : '#e2e8f0'}
                autoComplete="off"
              />
            ))}
          </div>
          <div style={{
            fontSize: '13px',
            color: '#94a3b8',
            marginTop: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}>
            <Clock size={14} />
            Enter the 4‑digit code sent to your phone.
          </div>
        </div>

        {/* Resend Button */}
        <div style={{ marginBottom: '24px' }}>
          <button
            type="button"
            onClick={handleResendClick}
            disabled={isResending || countdown > 0 || loading}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '10px',
              backgroundColor: (isResending || countdown > 0) ? '#e2e8f0' : 'transparent',
              color: (isResending || countdown > 0) ? '#94a3b8' : '#d4af37',
              border: (isResending || countdown > 0) ? '2px solid #e2e8f0' : '2px solid #d4af37',
              cursor: (isResending || countdown > 0) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
            onMouseEnter={(e) => {
              if (!isResending && countdown === 0 && !loading) {
                e.target.style.backgroundColor = '#fef9e7';
              }
            }}
            onMouseLeave={(e) => {
              if (!isResending && countdown === 0 && !loading) {
                e.target.style.backgroundColor = 'transparent';
              }
            }}
          >
            {isResending ? (
              <>
                <LoaderCircle size={20} style={{ animation: 'spin 1s linear infinite' }} />
                Sending...
              </>
            ) : countdown > 0 ? (
              <>
                <Clock size={20} />
                Resend available in {countdown}s
              </>
            ) : (
              <>
                <RefreshCw size={20} />
                Resend OTP
              </>
            )}
          </button>
        </div>

        {/* Verify Button */}
        <button
          type="submit"
          disabled={loading || isVerifying || !otpSent}
          style={{
            width: '100%',
            padding: '18px',
            fontSize: '18px',
            fontWeight: '700',
            borderRadius: '10px',
            backgroundColor: (!otpSent || loading || isVerifying) ? '#e2e8f0' : '#d4af37',
            color: (!otpSent || loading || isVerifying) ? '#94a3b8' : '#1a1a2e',
            border: 'none',
            cursor: (!otpSent || loading || isVerifying) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}
          onMouseEnter={(e) => {
            if (otpSent && !loading && !isVerifying) {
              e.target.style.backgroundColor = '#c5a032';
            }
          }}
          onMouseLeave={(e) => {
            if (otpSent && !loading && !isVerifying) {
              e.target.style.backgroundColor = '#d4af37';
            }
          }}
        >
          {loading || isVerifying ? (
            <>
              <LoaderCircle size={22} style={{ animation: 'spin 1s linear infinite' }} />
              Verifying...
            </>
          ) : (
            <>
              <Send size={20} /> Verify OTP
            </>
          )}
        </button>

        {/* Back to Login Link */}
        <div style={{
          textAlign: 'center',
          marginTop: '24px'
        }}>
          <Link to="/login" style={{
            color: '#64748b',
            textDecoration: 'none',
            fontSize: '15px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.color = '#d4af37'}
          onMouseLeave={(e) => e.target.style.color = '#64748b'}
          >
            <ArrowLeft size={18} /> Back to Sign In
          </Link>
        </div>
      </form>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </AuthLayout>
  );
};

export default VerifyOtp;